import { Box, Stack, alpha, useTheme } from "@mui/material";
import { amber, blue, teal } from "@mui/material/colors";
import { useCallback, useEffect, type FunctionComponent } from "react";
import { useUpdateNodeInternals, type NodeProps } from "reactflow";
import type NodeData from "../features/NodeData";
import { idleWalkthroughState } from "../features/NodeWalkthroughState";
import { formatNumber } from "../features/NumberFormatter";
import CollapsedInputHandles from "./CollapsedInputHandles";
import InputItems from "./InputItems";
import NodeTitle from "./NodeTitle";
import OutputHandle from "./OutputHandle";
import OutputItems from "./OutputItems";
import {
  bodyPadding,
  collapsedHandleSpacing,
  frameBorderRadius,
  frameBorderWidth,
  handleHoverScale,
  handleSize,
  itemHeight,
  inputWidth,
  manualReadyColor,
  nodeBorderDarkColor,
  nodeBorderLightColor,
  stepDerivativeColor,
  stepDoneColor,
  stepValueColor,
} from "./styles";

interface CustomNodeProps extends NodeProps {
  id: string;
  data: NodeData;
  selected: boolean;
}

const CustomNode: FunctionComponent<CustomNodeProps> = ({
  id,
  data,
  selected,
}) => {
  const theme = useTheme();
  const updateNodeInternals = useUpdateNodeInternals();

  const walkthroughState = data.walkthroughState ?? idleWalkthroughState;
  // A run is on, whichever one it is: that is all the dimming below cares about
  const isEvaluating = walkthroughState.status !== "idle";
  const isReadyToCompute = walkthroughState.status === "ready";
  const isDarkMode = data.isDarkMode;
  const isCollapsed = data.isCollapsed === true;

  useEffect(() => {
    updateNodeInternals(id);
  }, [
    id,
    updateNodeInternals,
    data.inputItems,
    data.outputItems,
    data.isCollapsed,
    data.name,
    data.decimalPlaces,
    data.isNodeValuesVisible,
    data.isNodeDerivativesVisible,
    data.topologicalOrder,
    data.isTopologicalOrderVisible,
  ]);

  /**
   * The single value worth keeping in sight while the node is collapsed.
   *
   * Operation nodes show their `f()` result. Constant and variable nodes have
   * no output item for it, so their value comes from the input field instead —
   * to the reader it's the same number either way.
   *
   * @returns The value to show, or null when the node has none.
   */
  const getCollapsedValue = useCallback((): string | null => {
    const valueOutputItem = data.outputItems.find(
      (outputItem) => outputItem.type === "value",
    );
    const decimalPlaces = data.decimalPlaces ?? null;
    if (valueOutputItem !== undefined) {
      if (data.isNodeValuesVisible === false) {
        return null;
      }
      // Step mode hides the value until the run reaches this node, and
      // collapsing must not leak what stepping is holding back
      return valueOutputItem.isHidden === true
        ? "?"
        : formatNumber(valueOutputItem.value, decimalPlaces);
    }

    // A collapsed constant or variable shows its input field's value, which is
    // read-only while collapsed and so safe to format
    const valueInputItem = data.inputItems.find((item) => item.id === "value");
    return valueInputItem === undefined
      ? null
      : formatNumber(valueInputItem.value, decimalPlaces);
  }, [
    data.decimalPlaces,
    data.inputItems,
    data.isNodeValuesVisible,
    data.outputItems,
  ]);

  // The collapsed handles split the frame into one slot per port, so the frame
  // needs a slot's worth of height for each of them
  const collapsedMinHeight =
    (data.inputItems.filter((item) => item.showHandle).length + 1) *
    collapsedHandleSpacing;

  /**
   * The one color that identifies the node type.
   *
   * Everything else about the node is neutral, so this single accent carries
   * the whole type distinction: it tints the header, colors the type icon and
   * fills the connection dots.
   */
  const getAccentColor = useCallback((): string => {
    const shade = isDarkMode ? 300 : 600;
    switch (data.featureNodeType.nodeType) {
      case "constant":
        return teal[shade];
      case "variable":
        return amber[shade];
      case "operation":
        return blue[shade];
    }
  }, [data.featureNodeType.nodeType, isDarkMode]);

  const accentColor = getAccentColor();

  /**
   * Color of the glow ring while the node is being evaluated. The derivative
   * phase uses a different color than the value phase so that the two passes
   * never look alike.
   */
  const getStepGlowColor = useCallback((): string | null => {
    if (walkthroughState.status !== "active") {
      return null;
    }
    return walkthroughState.phase === "derivative"
      ? stepDerivativeColor
      : stepValueColor;
  }, [walkthroughState]);

  /**
   * Border color of the frame.
   *
   * The border width never changes, so selecting a node can't shift the layout
   * by a pixel; only the color and the surrounding ring do.
   */
  const getBorderColor = useCallback((): string => {
    const glowColor = getStepGlowColor();
    if (glowColor !== null) {
      return glowColor;
    }
    if (walkthroughState.status === "done") {
      return stepDoneColor;
    }
    // Ranked under the two run colors: what the node already is beats what the
    // user could do to it next
    if (isReadyToCompute) {
      return manualReadyColor;
    }
    if (selected) {
      return accentColor;
    }
    return isDarkMode ? nodeBorderDarkColor : nodeBorderLightColor;
  }, [
    accentColor,
    getStepGlowColor,
    isDarkMode,
    isReadyToCompute,
    selected,
    walkthroughState.status,
  ]);

  const getBoxShadow = useCallback((): string => {
    const restingShadow = isDarkMode
      ? "0 1px 2px rgba(0, 0, 0, 0.5)"
      : "0 1px 2px rgba(16, 24, 40, 0.06)";
    const glowColor = getStepGlowColor();
    if (glowColor !== null) {
      return `0 0 0 3px ${alpha(glowColor, 0.28)}, ${restingShadow}`;
    }
    if (isReadyToCompute) {
      return `0 0 0 3px ${alpha(manualReadyColor, 0.26)}, ${restingShadow}`;
    }
    if (selected) {
      return `0 0 0 3px ${alpha(accentColor, 0.22)}, ${restingShadow}`;
    }
    return restingShadow;
  }, [accentColor, getStepGlowColor, isDarkMode, isReadyToCompute, selected]);

  const handleNameChange = useCallback(
    (name: string): void => {
      data.onNameChange(id, name);
    },
    [data, id],
  );

  const handleInputChange = useCallback(
    (inputPortId: string, value: string): void => {
      data.onInputChange(id, inputPortId, value);
    },
    [data, id],
  );

  const handleBodyClick = useCallback((): void => {
    data.onBodyClick(id);
  }, [data, id]);

  const handleToggleCollapse = useCallback((): void => {
    data.onToggleCollapse(id);
  }, [data, id]);

  const backgroundColor = theme.palette.background.paper;
  const dividerColor = theme.palette.divider;
  // The derivative target gets a stronger header tint on top of the flag icon
  const titleColor = alpha(
    data.isHighlighted ? theme.palette.primary.main : accentColor,
    isDarkMode ? 0.18 : 0.08,
  );

  return (
    /* Node frame */
    <Box
      className={
        walkthroughState.status === "active" || isReadyToCompute
          ? "step-pulse"
          : ""
      }
      bgcolor={backgroundColor}
      // Anchors the collapsed handles to the frame's own edges
      position="relative"
      sx={{
        border: `${frameBorderWidth}px solid ${getBorderColor()}`,
        // px strings only: a bare number would be multiplied by
        // theme.shape.borderRadius instead of being taken literally
        borderRadius: `${frameBorderRadius}px`,
        boxShadow: getBoxShadow(),
        // Dims the nodes a run hasn't reached yet so that the eye goes to the
        // active one. A node the manual mode is waiting on the user to pick
        // isn't one of them: it has its own status, and dimming the one thing
        // that's clickable would say the opposite of what it means
        opacity:
          isEvaluating && walkthroughState.status === "waiting" ? 0.55 : 1,
        cursor: isReadyToCompute ? "pointer" : undefined,
        // A collapsed node is only as tall as its header, which is too short
        // to keep several handles apart, so it grows with the port count
        minHeight: isCollapsed ? collapsedMinHeight : undefined,
        transition:
          "box-shadow 160ms ease-out, border-color 160ms ease-out, opacity 160ms ease-out",
      }}
    >
      <Stack>
        {/* Header */}
        <NodeTitle
          id={id}
          name={data.name}
          operationData={data.operationData}
          backgroundColor={titleColor}
          // The divider only makes sense with a body under it
          borderBottomColor={isCollapsed ? undefined : dividerColor}
          accentColor={accentColor}
          isHighlighted={data.isHighlighted}
          borderRadius={
            isCollapsed
              ? `${frameBorderRadius - frameBorderWidth}px`
              : `${frameBorderRadius - frameBorderWidth}px ${
                  frameBorderRadius - frameBorderWidth
                }px 0px 0px`
          }
          featureNodeType={data.featureNodeType}
          walkthroughState={walkthroughState}
          isCollapsed={isCollapsed}
          collapsedValue={isCollapsed ? getCollapsedValue() : null}
          topologicalOrder={data.topologicalOrder}
          isTopologicalOrderVisible={data.isTopologicalOrderVisible}
          onNameChange={handleNameChange}
          onToggleCollapse={handleToggleCollapse}
        />

        {/* Body */}
        {!isCollapsed && (
          <Box
            onClick={handleBodyClick}
            sx={{ cursor: isReadyToCompute ? "pointer" : "default" }}
          >
            {/* Input items */}
            {data.inputItems.length > 0 && (
              <Box p={bodyPadding}>
                <InputItems
                  id={id}
                  data={data}
                  itemHeight={itemHeight}
                  inputWidth={inputWidth}
                  // The body padding plus the frame border is the distance
                  // from an input row to the frame's left edge, which is where
                  // the dot belongs
                  handleLeftOffset={`calc(-1 * ${theme.spacing(
                    bodyPadding,
                  )} - ${frameBorderWidth}px)`}
                  handleSize={handleSize}
                  handleHoverScale={handleHoverScale}
                  handleColor={accentColor}
                  handleHoverColor={theme.palette.primary.main}
                  handleBorderColor={backgroundColor}
                  onInputChange={handleInputChange}
                />
              </Box>
            )}

            {/* Output items */}
            {data.outputItems.some((item) => {
              if (item.type === "value" && data.isNodeValuesVisible === false) {
                return false;
              }
              if (
                item.type === "derivative" &&
                data.isNodeDerivativesVisible === false
              ) {
                return false;
              }
              return true;
            }) && (
              <Box borderTop={1} borderColor="divider" p={bodyPadding}>
                <OutputItems
                  id={id}
                  data={data}
                  itemHeight={itemHeight}
                  inputWidth={inputWidth}
                />
              </Box>
            )}
          </Box>
        )}
      </Stack>

      {/* Input handles of a collapsed node, which has no input rows to hold
        them */}
      {isCollapsed && (
        <CollapsedInputHandles
          inputItems={data.inputItems}
          handleSize={handleSize}
          handleHoverScale={handleHoverScale}
          handleColor={accentColor}
          handleHoverColor={theme.palette.primary.main}
          handleBorderColor={backgroundColor}
        />
      )}

      {/* Output handle */}
      <OutputHandle
        handleSize={handleSize}
        handleHoverScale={handleHoverScale}
        handleColor={accentColor}
        handleHoverColor={theme.palette.primary.main}
        handleBorderColor={backgroundColor}
      />
    </Box>
  );
};

export default CustomNode;
